/**
 * SprintCard Component
 * AIDLC Administrative Backend
 *
 * Individual card on the Kanban sprint board.
 * Shows title, assignee avatar circle, priority badge, blocker icon.
 * Supports HTML5 drag-and-drop as a draggable element.
 *
 * Dimensions per Maya's spec: w-full min-h-[80px] p-3 rounded-lg border.
 */

import { Badge } from '../../shared/Badge';
import type { SprintBoardItem } from '../../../types/projects';

interface SprintCardProps {
  item: SprintBoardItem;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, itemId: string) => void;
}

const priorityVariants: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'neutral',
};

/** Get initials from an agent ID string like "marcus-chen" -> "MC" */
function getInitials(agentId: string | null): string {
  if (!agentId) return '?';
  return agentId
    .split('-')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

export function SprintCard({ item, onDragStart }: SprintCardProps) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item.id)}
      className="w-full min-h-[80px] p-3 rounded-lg border border-zinc-200 bg-white
                 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing
                 dark:bg-zinc-900 dark:border-zinc-800"
    >
      {/* Title -- truncated to 2 lines */}
      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-2 mb-2">
        {item.title}
      </p>

      {/* Bottom row: assignee, priority, blocker */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {/* Assignee avatar circle */}
          <div
            className="w-6 h-6 rounded-full bg-ldaja-blue/10 text-ldaja-blue flex items-center justify-center text-[9px] font-bold flex-shrink-0"
            title={item.assignedAgent || 'Unassigned'}
          >
            {getInitials(item.assignedAgent)}
          </div>

          {/* Priority badge */}
          <Badge variant={priorityVariants[item.priority] || 'neutral'}>
            {item.priority}
          </Badge>
        </div>

        {/* Blocker icon */}
        {item.hasBlocker && (
          <div className="flex-shrink-0" title="This item has a blocker">
            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        )}
      </div>

      {/* Review badges */}
      {(item.codeReview || item.qaReview) && (
        <div className="flex items-center gap-1.5 mt-1.5">
          {item.codeReview && (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                item.codeReview.rating >= 2
                  ? 'bg-green-50 text-green-700'
                  : item.codeReview.rating >= 0
                  ? 'bg-yellow-50 text-yellow-700'
                  : 'bg-red-50 text-red-700'
              }`}
              title={item.codeReview.comment || `Code review: ${item.codeReview.rating > 0 ? '+' : ''}${item.codeReview.rating}`}
            >
              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              {item.codeReview.rating > 0 ? '+' : ''}{item.codeReview.rating}
            </span>
          )}
          {item.qaReview && (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                item.qaReview.status === 'pass'
                  ? 'bg-green-50 text-green-700'
                  : item.qaReview.status === 'fail'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-gray-50 text-gray-500'
              }`}
              title={item.qaReview.comment || `QA: ${item.qaReview.status.toUpperCase()}`}
            >
              {item.qaReview.status === 'pass' ? (
                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ) : item.qaReview.status === 'fail' ? (
                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : null}
              QA
            </span>
          )}
        </div>
      )}

      {/* Story points if available */}
      {item.storyPoints !== null && item.storyPoints !== undefined && (
        <div className="mt-1.5 text-[10px] text-gray-400">
          {item.storyPoints} pt{item.storyPoints !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
